import React, { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AppShell from '../components/AppShell';
import { useAuth } from '../hooks/useAuth';
import {
  APP_NAV_ITEMS,
  canAccessNavItem,
  getAccessibleNavItems,
} from '../navigation/appNavigation';
import PrivateRoute, { CHANGE_PASSWORD_PATH } from './PrivateRoute';

/**
 * The route map (Rule 56 — every page lazy, every chunk its own).
 *
 * In-shell pages are NOT listed here twice: `APP_NAV_ITEMS` is iterated to build
 * them, so the sidebar and the router can never disagree about what exists. A new
 * module is one nav entry plus one arm in `renderModuleRoute`.
 *
 * Standalone routes below the loop are the ones with no nav entry — account
 * pages you reach from the avatar menu, and the public referral page, which is
 * outside the shell entirely because its visitor has no account.
 */

// ── Public / auth ────────────────────────────────────────────────────────────
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('../pages/VerifyEmailPage'));
const OAuthCallbackPage = React.lazy(() => import('../pages/OAuthCallbackPage'));
const PublicRegisterPage = React.lazy(() => import('../pages/PublicRegisterPage'));

// ── RMC2 core ────────────────────────────────────────────────────────────────
const DashboardPage = React.lazy(() => import('../pages/DashboardPage'));
const MembersPage = React.lazy(() => import('../pages/MembersPage'));
const BranchesPage = React.lazy(() => import('../pages/BranchesPage'));
const BillingLedgerPage = React.lazy(() => import('../pages/BillingLedgerPage'));
const ClaimsDeskPage = React.lazy(() => import('../pages/ClaimsDeskPage'));
const HouseOverridesPage = React.lazy(() => import('../pages/HouseOverridesPage'));
const NetworkPayoutsPage = React.lazy(() => import('../pages/NetworkPayoutsPage'));
const SupportPage = React.lazy(() => import('../pages/SupportPage'));
const ReportsPage = React.lazy(() => import('../pages/ReportsPage'));
const BankHolidaysPage = React.lazy(() => import('../pages/BankHolidaysPage'));
const BenefitCatalogPage = React.lazy(() => import('../pages/BenefitCatalogPage'));

// ── Member portal ────────────────────────────────────────────────────────────
const MemberBenefitsPage = React.lazy(() => import('../pages/MemberBenefitsPage'));
const MemberProfilePage = React.lazy(() => import('../pages/MemberProfilePage'));
const MemberBillingPage = React.lazy(() => import('../pages/MemberBillingPage'));
const MemberClaimsPage = React.lazy(() => import('../pages/MemberClaimsPage'));
const MemberNetworkPage = React.lazy(() => import('../pages/MemberNetworkPage'));

// ── Platform administration (inherited) ──────────────────────────────────────
const OauthUsersPage = React.lazy(() => import('../pages/OauthUsersPage'));
const OauthClientsPage = React.lazy(() => import('../pages/OauthClientsPage'));
const SystemSettingsPage = React.lazy(() => import('../pages/SystemSettingsPage'));
const UploadS3PublicShareTokenPage = React.lazy(() => import('../pages/UploadS3PublicShareTokenPage'));
const AuditLogsPage = React.lazy(() => import('../pages/AuditLogsPage'));
const FieldHistoryPage = React.lazy(() => import('../pages/FieldHistoryPage'));
const LoginAttemptsPage = React.lazy(() => import('../pages/LoginAttemptsPage'));
const OutboundHttpLogsPage = React.lazy(() => import('../pages/OutboundHttpLogsPage'));
const FailedJobsPage = React.lazy(() => import('../pages/FailedJobsPage'));

// ── Account (reached from the avatar menu, not the sidebar) ──────────────────
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));
const SessionsPage = React.lazy(() => import('../pages/SessionsPage'));
const ChangePasswordPage = React.lazy(() => import('../pages/ChangePasswordPage'));
const MfaSettingsPage = React.lazy(() => import('../pages/MfaSettingsPage'));
const AccountSecurityPage = React.lazy(() => import('../pages/AccountSecurityPage'));
const LinkedAccountsPage = React.lazy(() => import('../pages/LinkedAccountsPage'));
const CompleteProfilePage = React.lazy(() => import('../pages/CompleteProfilePage'));

function RouteFallback() {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '12rem',
        height: '100%',
        fontSize: '0.85rem',
        color: 'var(--muted)',
      }}
    >
      {t('app.loading')}
    </div>
  );
}

function withRouteSuspense(element) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

/** Maps a nav entry's path to its page. One arm per module. */
function renderModuleRoute(item) {
  switch (item.path) {
    case '/dashboard': return withRouteSuspense(<DashboardPage />);

    // Member portal
    case '/portal/profile': return withRouteSuspense(<MemberProfilePage />);
    case '/portal/benefits': return withRouteSuspense(<MemberBenefitsPage />);
    case '/portal/billing': return withRouteSuspense(<MemberBillingPage />);
    case '/portal/claims': return withRouteSuspense(<MemberClaimsPage />);
    case '/portal/network': return withRouteSuspense(<MemberNetworkPage />);

    // Administration
    case '/members': return withRouteSuspense(<MembersPage />);
    case '/branches': return withRouteSuspense(<BranchesPage />);
    case '/billing': return withRouteSuspense(<BillingLedgerPage />);
    case '/claims': return withRouteSuspense(<ClaimsDeskPage />);
    case '/overrides': return withRouteSuspense(<HouseOverridesPage />);
    case '/network': return withRouteSuspense(<NetworkPayoutsPage />);
    case '/support': return withRouteSuspense(<SupportPage />);
    case '/reports': return withRouteSuspense(<ReportsPage />);

    // Settings
    case '/settings/bank-holidays': return withRouteSuspense(<BankHolidaysPage />);
    case '/settings/benefit-catalog': return withRouteSuspense(<BenefitCatalogPage />);

    // Platform administration
    case '/admin/users': return withRouteSuspense(<OauthUsersPage />);
    case '/admin/oauth-clients': return withRouteSuspense(<OauthClientsPage />);
    case '/admin/system-settings': return withRouteSuspense(<SystemSettingsPage />);
    case '/admin/upload-s3-public-share-tokens': return withRouteSuspense(<UploadS3PublicShareTokenPage />);
    case '/admin/audit-logs': return withRouteSuspense(<AuditLogsPage />);
    case '/admin/field-history': return withRouteSuspense(<FieldHistoryPage />);
    case '/admin/login-attempts': return withRouteSuspense(<LoginAttemptsPage />);
    case '/admin/outbound-http-logs': return withRouteSuspense(<OutboundHttpLogsPage />);
    case '/admin/failed-jobs': return withRouteSuspense(<FailedJobsPage />);

    default:
      // A nav entry with no page is a wiring bug, not a user error — say so
      // plainly rather than rendering a blank panel.
      return (
        <div className="rmc2-empty" role="status">
          <p className="rmc2-empty-title">{item.label}</p>
          <p className="rmc2-empty-desc">
            This screen is registered in navigation but has no page bound to it yet.
          </p>
        </div>
      );
  }
}

export default function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  const userScopes = useMemo(
    () => (Array.isArray(user?.scopes) ? user.scopes : []),
    [user?.scopes],
  );

  const fallbackPath = useMemo(
    () => getAccessibleNavItems(userScopes)[0]?.path ?? '/dashboard',
    [userScopes],
  );

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? fallbackPath : '/login'} replace />}
      />

      {/* Public */}
      <Route path="/login" element={withRouteSuspense(<LoginPage />)} />
      <Route path="/forgot-password" element={withRouteSuspense(<ForgotPasswordPage />)} />
      <Route path="/reset-password" element={withRouteSuspense(<ResetPasswordPage />)} />
      <Route path="/verify-email" element={withRouteSuspense(<VerifyEmailPage />)} />
      <Route path="/oauth/callback" element={withRouteSuspense(<OAuthCallbackPage />)} />

      {/* The referral link + its QR code (Rule 77). Outside the shell and outside
          PrivateRoute — the whole point is that its visitor has no account. */}
      <Route path="/register/:referralToken" element={withRouteSuspense(<PublicRegisterPage />)} />

      <Route element={<PrivateRoute />}>
        <Route element={<AppShell />}>
          {APP_NAV_ITEMS.map((item) => (
            <Route
              key={item.path}
              path={item.path}
              element={
                canAccessNavItem(userScopes, item)
                  ? renderModuleRoute(item)
                  : <Navigate to={fallbackPath} replace />
              }
            />
          ))}

          <Route path="/profile" element={withRouteSuspense(<ProfilePage />)} />
          <Route path="/profile/sessions" element={withRouteSuspense(<SessionsPage />)} />
          <Route path={CHANGE_PASSWORD_PATH} element={withRouteSuspense(<ChangePasswordPage />)} />
          <Route path="/profile/mfa" element={withRouteSuspense(<MfaSettingsPage />)} />
          <Route path="/profile/account-security" element={withRouteSuspense(<AccountSecurityPage />)} />
          <Route path="/profile/linked-accounts" element={withRouteSuspense(<LinkedAccountsPage />)} />
          <Route path="/profile/complete" element={withRouteSuspense(<CompleteProfilePage />)} />
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? fallbackPath : '/login'} replace />}
      />
    </Routes>
  );
}
